<template>
  <div class="aco-grid">
    <div class="aco-info-box">
      <b-card>
        <margin-fields :fields="marginFields()"/>
        <b-form-group id="preferredLanguageGroup" label-for="preferredLanguage">
          <template slot="label">
            {{ $t('preferredLanguage') }}
            <font-awesome-icon id="metadataPreferredLanguageTip" icon="info-circle"/>
          </template>
          <b-form-select :id="preferredLanguage" v-model="preferredLanguage" :options="preferredLanguages" @change="updatePreferredLanguage"/>
        </b-form-group>
        <b-button block id="updateCrystallize" @click="openUpdateCrystallizeModal" :disabled="!product">Send til Crystallize</b-button>
        <b-button id="showEvents" type="submit" block :disabled="isShowEventButtonEnabled" v-on:click="showEvents()">
          {{ $t('showEvents') }}
        </b-button>
        <b-button block v-on:click="licenseSearch()">{{ $t('licenseSearch') }}</b-button>
        <show-events ref="showEvents" :events="events"/>
        <b-button type="button" id="localProductDelete" @click="openLocalProductDeleteModal" :disabled=!isLocalProduct block variant="danger">
          {{ $t('delete') }}
        </b-button>
        <local-product-delete-modal ref="localProductDeleteModal"/>
        <update-crystallize-modal ref="updateCrystallizeModal"/>
      </b-card>

      <b-tooltip target="metadataPreferredLanguageTip">{{ $t('toolTip.metadataPreferredLanguageTip') }}</b-tooltip>
    </div>

    <div>
      <div class="aco-details-box">
        <b-col class="aco-details">
          <details-form v-if="product.source === 'LOKUS_MAGENTO'" :fields="lokusMagentoInfoFields()" :state-object="product"/>
          <details-form v-if="product.source === 'ACO_BIBLIO'" :fields="biblioInfoFields()" :state-object="product"/>
          <details-form v-if=isLocalProduct :fields="localInfoFields()" :state-object="product"/>
          <b-row>
            <b-col md="12">
              <b-button type="button" class="float-left mt-3" @click="loadProduct()">{{ $t('reset') }}</b-button>
              <b-button type="button" variant="primary" class="float-right mt-3" @click="saveProduct()">{{ $t('save') }}</b-button>
            </b-col>
          </b-row>
        </b-col>
      </div>

      <div v-if="!hasParentProducts() && (isProductBundle() || supportsEditingBundles(product.source))" class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col md="12">
              <h5>{{ $t('childProducts') }}</h5>
            </b-col>
            <b-table striped hover :items=bundledProducts :fields="productListFields()" class="bg-white table-condensed mx-3">
              <template v-slot:cell(show_product)="row">
                <router-link :to="{ name: 'ProductDetails', params: { source: row.item.source, sourceId: row.item.sourceId }}">
                  <b-button size="sm" class="py-0 mr-1">{{ $t('openProduct') }}</b-button>
                </router-link>
                <b-button size="sm" class="py-0 mr-2" v-if=supportsEditingBundles(product.source) @click="removeProduct(row.item.sourceId)">
                  {{ $t('remove') }}
                </b-button>
              </template>
            </b-table>
            <b-col md="12">
              <b-button type="button" class="float-left mt-3" v-if=supportsEditingBundles(product.source) @click="openProductSearchModal">
                {{ $t('add') }}
              </b-button>
              <product-search ref="productSearch" @selectedEan="addProductToBundledProduct" :data="{ source: product.source, noBundles: true }"/>
              <b-button type="button" variant="primary" class="float-right mt-3" v-if=supportsEditingBundles(product.source)
                        @click="saveBundledProduct(product)">{{ $t('save') }}
              </b-button>
            </b-col>
          </b-row>
        </b-col>
      </div>

      <div v-if="hasParentProducts()" class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col md="12">
              <h5>{{ $t('parentProducts') }}</h5>
            </b-col>
            <b-table striped hover :items="parentProducts" :fields="productListFields()" class="bg-white table-condensed mx-3">
              <template v-slot:cell(show_product)="row">
                <router-link :to="{ name: 'ProductDetails', params: { source: row.item.source, sourceId: row.item.sourceId }}">
                  <b-button size="sm" class="py-0">{{ $t('openProduct') }}</b-button>
                </router-link>
              </template>
            </b-table>
          </b-row>
        </b-col>
      </div>

      <div v-if="metadata && metadata.ean" class="aco-details-box">
        <b-col class="aco-details">
          <details-form :fields="metadataFields()" :state-object="metadata"/>
          <details-form v-if="metadata.aunivers !== null" :fields="auniversMetadataFields()" :state-object="metadata.aunivers"/>
        </b-col>
      </div>
      <div v-else class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col md="12">
              <h5>{{ $t('metadata') }}</h5>
              <p>{{ $t('noMetadata') }}</p>
            </b-col>
          </b-row>
        </b-col>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import BaseComponent from '../../components/BaseComponent'
  import DetailsForm from '../../components/DetailsForm'
  import MarginFields from '../../components/MarginFields'
  import ShowEvents from '../../components/ShowEvents'
  import ProductSearch from '../../components/ProductSearch'
  import LocalProductDeleteModal from './LocalProductDeleteModal'
  import UpdateCrystallizeModal from '@/views/ProductPage/UpdateCrystallizeModal.vue';

  export default {
    name: 'ProductDetails',
    extends: BaseComponent,
    components: {
      'detailsForm': DetailsForm,
      'marginFields': MarginFields,
      ShowEvents,
      ProductSearch,
      LocalProductDeleteModal,
      UpdateCrystallizeModal,
    },
    computed: {
      ...mapGetters('products', ['product', 'isShowEventButtonEnabled', 'events', 'parentProducts', 'bundledProducts']),
      ...mapGetters('metadata', ['metadata']),
      isLocalProduct () {
        return (this.product.source === 'LOCAL')
      }
    },
    data () {
      return {
        preferredLanguage: null,
        ean: null
      }
    },
    methods: {
      updatePreferredLanguage (item) {
        this.$store.dispatch('metadata/getMetadata', { ean: this.product.ean, preferredLanguage: item })
      },
      marginFields () {
        return [
          { name: 'productSource', displayName: this.$t('productSource'), value: this.translateAlternatives('productSources', this.product.source) },
          { name: 'created', displayName: this.$t('created'), value: this.formatTimestamp(this.product.created) },
          { name: 'modified', displayName: this.$t('modified'), value: this.formatTimestamp(this.product.modified) }
        ]
      },
      lokusMagentoInfoFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('productInformation') },

          { type: 'text', name: 'name', readonly: true, width: 6 },
          { type: 'dropdown', name: 'site', readonly: false, width: 3, items: this.sites },
          { type: 'boolean', name: 'forSale', readonly: true, width: 3 },

          { type: 'text', name: 'ean', readonly: true, width: 2, displayName: this.$t('ean_isbn') },
          { type: 'text', name: 'sourceId', readonly: true, width: 2 },
          { type: 'text', name: 'forSaleFrom', readonly: true, width: 2 },
          { type: 'text', name: 'accessibleFrom', readonly: true, width: 2 },
          { type: 'boolean', name: 'active', readonly: false, width: 2 },

          { type: 'dropdown', name: 'productType', readonly: true, width: 4, items: this.productTypes },
          { type: 'dropdown', name: 'licenseType', readonly: true, width: 4, items: this.licenseTypes },
          { type: 'dropdown', name: 'licenseDuration', readonly: true, width: 4, items: this.licenseDurations }
        ]
      },
      biblioInfoFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('productInformation') },

          { type: 'text', name: 'name', readonly: true, width: 6 },
          { type: 'dropdown', name: 'site', readonly: true, width: 3, items: this.sites },
          { type: 'tags', name: 'languages', displayName: this.$t('language'), readonly: true, width: 3, items: this.languages },

          { type: 'text', name: 'ean', readonly: true, width: 3, displayName: this.$t('ean_isbn') },
          { type: 'text', name: 'sourceId', readonly: true, width: 3 },
          { type: 'boolean', name: 'forSale', readonly: true, width: 1 },
          { type: 'text', name: 'forSaleFrom', readonly: true, width: 2 },
          { type: 'text', name: 'accessibleFrom', readonly: true, width: 2 },
          { type: 'boolean', name: 'active', readonly: true, width: 1 },

          { type: 'text', name: 'series', readonly: true, width: 3, displayName: this.$t('series') },
          { type: 'text', name: 'bookGroup', readonly: true, width: 3, displayName: this.$t('bookGroup') },
          { type: 'text', name: 'priceExVat', readonly: true, width: 3, displayName: this.$t('priceExTax') },
          { type: 'text', name: 'price', readonly: true, width: 3 },

          { type: 'dropdown', name: 'productType', readonly: true, width: 3, items: this.productTypes },
          { type: 'dropdown', name: 'licenseType', readonly: true, width: 3, items: this.licenseTypes },
          { type: 'dropdown', name: 'licenseDuration', readonly: true, width: 3, items: this.licenseDurations },
          { type: 'boolean', name: 'marketPublishingStatusActive', readonly: true, width: 3, displayName: this.$t('marketingStatus') },

          { type: 'url', name: 'catalogUrl', width: 6 },
          { type: 'url', name: 'frontCoverUrl', width: 6 },

          { type: 'tags', name: 'subjects', readonly: true, width: 6 },
          { type: 'tags', name: 'grades', readonly: true, width: 6 },

          { type: 'text', name: 'author', readonly: true, width: 12 },

          { type: 'textarea', name: 'description', readonly: true, width: 12 },

          { type: 'text', name: 'previewUrl', readonly: true, width: 12, displayName: 'Bla i boka' },

        ]
      },
      metadataFields () {
        return [
          { type: 'header', backButton: false, width: 12, displayName: this.$t('metadata') },

          { type: 'dropdown', name: 'site', readonly: true, width: 4, items: this.sites },
          { type: 'url', name: 'metadataUrl', readonly: true, width: 8, displayName: this.$t('metadata') + ' URL' },

          { type: 'tags', name: 'grade', readonly: true, width: 4, displayName: this.$t('grades') },
          { type: 'tags', name: 'program', readonly: true, width: 4, displayName: this.$t('programs') },
          { type: 'tags', name: 'subject', readonly: true, width: 4, displayName: this.$t('subjects') },
        ]
      },
      auniversMetadataFields () {
        return [
          { type: 'header', backButton: false, width: 12, displayName: this.$t('aunivers') + ' ' + this.$t('metadata') },

          { type: 'text', name: 'name', readonly: true, width: 4 },
          { type: 'boolean', name: 'inDevelopment', readonly: true, width: 1 },
          { type: 'boolean', name: 'disabled', readonly: true, width: 1 },
          { type: 'text', name: 'locationId', readonly: true, width: 1 },
          { type: 'text', name: 'redirectLocation', readonly: true, width: 1, displayName: this.$t('redirectLocation') },
          { type: 'text', name: 'remoteId', readonly: true, width: 2 },
          { type: 'text', name: 'design', readonly: true, width: 2, displayName: this.$t('viewType') },

          { type: 'url', name: 'thumbnailUrl', readonly: true, width: 8, displayName: this.$t('thumbnail') + ' URL' },
          { type: 'text', name: 'thumbnailAlt', readonly: true, width: 3, displayName: this.$t('thumbnail') + ' ' + this.$t('text').toLowerCase() },
          { type: 'text', name: 'thumbnailColor', readonly: true, width: 1, displayName: this.$t('thumbnail') + ' ' + this.$t('color').toLowerCase() },

          { type: 'url', name: 'externalUrl', readonly: true, width: 12, displayName: this.$t('external') + ' URL' },
        ]
      },
      productListFields () {
        return [
          { key: 'source', label: this.$t('productSource'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('productSources', value) },
          { key: 'ean', label: this.$t('ean'), tdClass: 'text-nowrap' },
          { key: 'site', label: this.$t('site'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('sites', value) },
          { key: 'name', label: this.$t('name'), tdClass: 'text-nowrap' },
          { key: 'productType', label: this.$t('productType'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('productTypes', value) },
          { key: 'licenseType', label: this.$t('licenseType'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('licenseTypes', value) },
          { key: 'show_product', label: '' }
        ]
      },
      localInfoFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('productInformation') },

          { type: 'text', name: 'name', width: 6 },
          { type: 'dropdown', name: 'site', width: 3, items: this.sites },
          { type: 'tags', name: 'languages', displayName: this.$t('language'), readonly: false, width: 3, items: this.languages },

          { type: 'text', name: 'ean', readonly: true, width: 6, displayName: this.$t('ean_isbn') },
          { type: 'text', name: 'sourceId', readonly: true, width: 3 },

          { type: 'dropdown', name: 'productType', width: 2, items: this.productTypes },
          { type: 'dropdown', name: 'licenseType', width: 2, items: this.licenseTypes },
          { type: 'dropdown', name: 'licenseDuration', width: 2, items: this.licenseDurations },
          { type: 'boolean', name: 'forSale', width: 1 },
          { type: 'date', name: 'forSaleFrom', width: 2 },
          { type: 'date', name: 'accessibleFrom', width: 2 },
          { type: 'boolean', name: 'active', width: 1 },

          { type: 'number', name: 'price', width: 1 },
          { type: 'text', name: 'catalogUrl', width: 5 },
          { type: 'text', name: 'frontCoverUrl', width: 6 },

          {
            type: 'dropdown',
            name: 'subjects',
            width: 6,
            items: this.subjects,
            multiple: true,
            selectSize: 8,
            appendToolTip: true,
            toolTip: 'toolTip.userProfileRow'
          },
          {
            type: 'dropdown',
            name: 'grades',
            width: 6,
            items: this.grades,
            multiple: true,
            selectSize: 8,
            appendToolTip: true,
            toolTip: 'toolTip.userProfileRow'
          },

          { type: 'text', name: 'author', width: 2 },
          { type: 'text', name: 'description', width: 10 }
        ]
      },
      isProductBundle () {
        return this.bundledProducts && this.bundledProducts.length !== 0
      },
      saveProduct () {
        this.$store.dispatch('products/createOrUpdateProductBySourceId', this.$route.params.sourceId)
      },
      loadProduct () {
        this.$store.dispatch(
          'products/getProductBySourceId',
          {
            source: this.$route.params.source, sourceId: this.$route.params.sourceId, preferredLanguage: this.preferredLanguage
          })
      },
      showEvents () {
        this.$root.$emit('bv::show::modal', 'showEvents')
      },
      loadParentProducts () {
        this.$store.dispatch('products/getParentProducts', { source: this.$route.params.source, sourceId: this.$route.params.sourceId })
      },
      hasParentProducts () {
        return this.parentProducts && this.parentProducts.length !== 0
      },
      updateProduct () {
        this.$store.dispatch('products/resetProduct')
        this.$store.dispatch('metadata/resetMetadata')
        this.loadProduct()
        const objectReference = 'product:sourceId:' + this.$route.params.source + '/' + this.$route.params.sourceId
        this.$store.dispatch('products/getObjectReference', objectReference)
        this.loadParentProducts()
      },
      openProductSearchModal () {
        this.$root.$emit('bv::show::modal', 'productSearch')
      },
      removeProduct (sourceId) {
        this.$store.dispatch('products/removeBundledProduct', sourceId)
      },
      addProductToBundledProduct (product) {
        this.$store.dispatch('products/addBundledProduct', product)
      },
      saveBundledProduct (product) {
        this.$store.dispatch('products/updateBundledProducts', product)
      },
      supportsEditingBundles (source) {
        return source === 'ACO_BIBLIO' || source === 'LOCAL'
      },
      licenseSearch () {
        this.$store.dispatch('licenses/resetFilter')
        let payload = {
          ean: this.product.ean,
        }
        this.$store.dispatch('licenses/getLicensesByCustomerOrgNumberOrUserIdOrEan', payload)
          .then(() => {
            this.$router.push('/licenses')
          })
      },
      openLocalProductDeleteModal () {
        this.$root.$emit('bv::show::modal', 'localProductDeleteModal')
      },
      openUpdateCrystallizeModal() {
        this.$root.$emit('bv::show::modal', 'updateCrystallizeModal')
      },
    },
    mounted () {
      this.updateProduct()
    },
    watch: {
      $route () {
        this.updateProduct()
      }
    },
    i18n: {
      messages: {
        en: {
          'productInformation': 'Product information',
          'noMetadata': 'Metadata was not found for this product.',
          'parentProducts': 'Parent products',
          'childProducts': 'Child products'
        },
        nb: {
          'productInformation': 'Produktinformasjon',
          'noMetadata': 'Metadata ble ikke funnet for dette produktet.',
          'parentProducts': 'Foreldreprodukter',
          'childProducts': 'Underprodukter'
        }
      }
    }
  }
</script>
